import React, { ReactNode, useEffect } from 'react'
import { AsyncState, Maybe, Template, Unit, unit } from 'ballerina-core'
import { AsyncLoaderTemplate } from '@shared/Frontend/asyncLoader'
import { getFilters } from './filters.api'
import { FiltersMap, FiltersState, initialFiltersState } from './filters.state'
import { TeaserKind } from '../teasers/teasers.state'
import { FiltersProps } from './filters.layout'
import { clearFilterQueryParameters, mapFilterToQueryParameters, mapQueryToFilters } from './filters.utils'
import { getSite } from '@shared/Frontend/utils/site'

export type FiltersContext = { type: TeaserKind }
export type FiltersView = {
  Loader: (_: { type?: 'inline' | 'full' }) => ReactNode
  Filters: (_: FiltersProps) => ReactNode
  None: () => ReactNode
}

export const debounceHook = (filters?: FiltersMap) => {
  if (!filters) return

  const debounceDelay = 1000
  const timeout = setTimeout(() => {
    mapFilterToQueryParameters(filters)
  }, debounceDelay)

  return () => {
    clearTimeout(timeout)
  }
}

const FiltersTemplate = Template.Default<FiltersContext, FiltersState, Unit, FiltersView>(
  ({ context, setState, view, foreignMutations }) => {
    const filters = AsyncState.Operations.hasValue(context) ? context.value : undefined
    const site = getSite()

    useEffect(() => debounceHook(filters), [filters])

    if (!filters) return <view.Loader type='inline' />
    if (site == 'ZEG' && context.type !== 'search') return <view.None />

    return <view.Filters
      kind={context.type}
      filters={filters}
      setFilter={(key, updater) => {
        setState(os => {
          if (!AsyncState.Operations.hasValue(os)) return os

          const asyncUpdater = AsyncState.Operations.map<Maybe<FiltersMap>, Maybe<FiltersMap>>(s0 => {
            if (!s0) return s0
            return s0.update(key, value => updater(value))
          })

          return asyncUpdater(os)
        })
      }}
      clearFilters={() => {
        setState(_ => {
          clearFilterQueryParameters()
          return initialFiltersState()
        })
      }}
    />
  })

export const FiltersWrapperTemplate = Template.Default<FiltersContext, FiltersState, Unit, FiltersView>(
  ({ context, setState, view }) => {
    return <>
      <FiltersLoaderTemplate
        context={{
          ...context, promise: async () => {
            const filtersResponse = await getFilters(context.type)
            const mappedFilters = mapQueryToFilters(filtersResponse)
            return mappedFilters
          }
        }}
        setState={setState}
        foreignMutations={unit}
        view={unit}
      />
      <FiltersTemplate
        context={context}
        setState={setState}
        foreignMutations={unit}
        view={view}
      />
    </>
  }
)

const FiltersLoaderTemplate = AsyncLoaderTemplate<Maybe<FiltersMap>>()
